import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {getMaxRowsConfig} from 'notebook/structured-tables/util/checkTableCapacity';
import {AssayResult} from 'results/assays/AssayRecordConstants';
import AssayRecordOriginLink, {getOriginText} from 'results/assays/AssayRecordOriginLink';
import {CellWrapper} from 'results/assays/AssayRecordRendering';
import {ResultViewType} from 'results/assays/AssayResultsConstants';
import ReviewStatusDisplay, {getReviewStatusText} from 'results/assays/ReviewStatusDisplay';
import withFilteredAssayResultsLoading, {
  FilteredAssayResultsConnectedProps,
} from 'results/assays/withFilteredAssayResultsLoading';
import AssayResultsTable from 'runs/AssayResultsTable';
import {FixedColumn} from 'runs/ColumnConstants';
import {ADDITIONAL_METADATA, DEFAULT_MIN_COLUMN_WIDTH} from 'runs/Columns';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import WarningIcon from 'ui-library/icons/WarningIcon';

import {Filters} from './FilteredAssayResultsActions';
import {RESULTS_TABLE_SPEC_SHAPE, ResultsTableSpec} from './ResultsTableSpecConstants';

interface Props {
  emptyMessage: string;
  filters: Filters;
  includeResultsAgainstArchivedSchemas: boolean;
  hideIfEmpty?: boolean;
  resultType?: ResultViewType;
}

interface State {
  tableCidPrefix: string;
}

function TruncatedWarning(): JSX.Element {
  return (
    <Tooltip
      text={`This table is limited to the ${getMaxRowsConfig(false)} latest results. Download as CSV to see all results.`}
    >
      <WarningIcon className={css(styles.warningIcon)} />
    </Tooltip>
  );
}

export class StandaloneResultsView extends React.Component<
  Props & FilteredAssayResultsConnectedProps,
  State
> {
  state: State = {
    tableCidPrefix: _.uniqueId('table'),
  };

  static propTypes = {
    emptyMessage: PropTypes.string.isRequired,
    filters: PropTypes.shape({
      bioentityId: PropTypes.string,
      requestId: PropTypes.string,
      storableId: PropTypes.string,
      studyId: PropTypes.string,
    }).isRequired,
    resultsTableSpecs: PropTypes.arrayOf(RESULTS_TABLE_SPEC_SHAPE.isRequired).isRequired,
    includeResultsAgainstArchivedSchemas: PropTypes.bool,
    resultType: PropTypes.string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any;

  _getAdditionalColumns(): Array<FixedColumn> {
    const sourceColumn: FixedColumn = {
      metadataType: ADDITIONAL_METADATA,
      label: 'Source',
      width: 200,
      render: (result: AssayResult) => ({
        formattedValue: (
          <CellWrapper>
            <AssayRecordOriginLink resultId={result.id} />
          </CellWrapper>
        ),
        text: getOriginText(result.id),
        align: 'left',
      }),
    };
    if (BenchlingConfig.RESULTS_FILTER_TO_REVIEWED_DATA_OUTSIDE_ENTRY) {
      return [sourceColumn];
    } else {
      return [
        {
          metadataType: ADDITIONAL_METADATA,
          label: 'Reviewed',
          width: DEFAULT_MIN_COLUMN_WIDTH,
          render: (result: AssayResult) => ({
            formattedValue: (
              <CellWrapper>
                <ReviewStatusDisplay resultId={result.id} />
              </CellWrapper>
            ),
            text: getReviewStatusText(result.id),
          }),
        },
        sourceColumn,
      ];
    }
  }

  render(): JSX.Element | null {
    const {emptyMessage, resultsTableSpecs, hideIfEmpty} = this.props;
    const sortedResultsTableSpecs = _.sortBy(resultsTableSpecs, (spec: ResultsTableSpec) => spec.schema.name);
    if (!sortedResultsTableSpecs.length) {
      if (hideIfEmpty) {
        return null;
      }
      return (
        <div className="u-faded" {...getTestProps({component: 'StandaloneResultsView', key: 'emptyMessage'})}>
          {emptyMessage}
        </div>
      );
    }
    return (
      <div
        className={classNames(
          'mediocre',
          'react-tab-container--mediocre-tableWrapper',
          'react-tab-container--mediocre-tableWrapper--noPaddingTop'
        )}
      >
        {sortedResultsTableSpecs.map((spec: ResultsTableSpec) => (
          <div key={spec.schema.id} className="mediocre-item is-table">
            <AssayResultsTable
              onDownloadAsCSV={spec.onDownloadAsCSV}
              schema={spec.schema}
              results={
                spec.assayResults as Array<AssayResult> /** TODO BNCH-48428 Filter DATA_MISSING values for assay results */
              }
              additionalColumns={this._getAdditionalColumns()}
              forceRowCount={spec.isOversized ? spec.total : undefined}
              isOversized={spec.isOversized}
              canRename={false}
              // Proxied-through properties
              {...standaloneProps}
              fileId=""
              headerControls={spec.isTruncated ? [<TruncatedWarning key="results-truncated-warning" />] : []}
              cid={this.state.tableCidPrefix + spec.schema.id}
              locked={true}
              updateDisplayConfigHandler={() => {}}
            />
          </div>
        ))}
      </div>
    );
  }
}

export default withFilteredAssayResultsLoading(StandaloneResultsView);

const styles = StyleSheet.create({
  warningIcon: {
    fontSize: 20,
    marginLeft: 8,
    verticalAlign: 'middle',
  },
});
